import { z } from 'zod';

/**
 * Event taxonomy for the ACES telemetry bus.
 *
 * Every student interaction emits one of these — the same schema will feed the
 * downstream LLM tutor (the Agentic layer), so it must stay stable, typed, and
 * decoupled from UI specifics.
 */

export const EventBaseSchema = z.object({
  sessionId: z.string().uuid(),
  ts: z.number().int().nonnegative(),    // epoch millis
  path: z.string().optional(),           // route at time of emission
});

const KindlessPayload = <K extends string, P extends z.ZodTypeAny>(kind: K, payload: P) =>
  z.object({ kind: z.literal(kind), payload });

/* ----- Session lifecycle ----- */
const SessionStart = KindlessPayload('session.start', z.object({}));
const SessionEnd   = KindlessPayload('session.end',   z.object({}));

/* ----- Navigation ----- */
const RouteEnter = KindlessPayload('route.enter', z.object({
  chapterId: z.string().optional(),
  board: z.string().optional(),
  grade: z.string().optional(),
  subject: z.string().optional(),
}));

/* ----- Simulation lifecycle ----- */
const SimMount  = KindlessPayload('sim.mount',  z.object({ simId: z.string(), chapterId: z.string() }));
const SimReset  = KindlessPayload('sim.reset',  z.object({ simId: z.string() }));

/* ----- Journal/Ledger specific (one example of a domain event family) ----- */
const TxnDrafted = KindlessPayload('txn.drafted', z.object({
  simId: z.string(),
  description: z.string(),
  debitAccount: z.string(),
  creditAccount: z.string(),
  amount: z.number(),
}));
const TxnPosted = KindlessPayload('txn.posted', z.object({
  simId: z.string(),
  entryId: z.string(),
  balanced: z.boolean(),
}));
const TxnRejected = KindlessPayload('txn.rejected', z.object({
  simId: z.string(),
  reason: z.enum(['unbalanced', 'invalid-account', 'zero-amount', 'self-loop']),
}));

/* ----- Conceptual struggle signals (what the future tutor reasons over) ----- */
const ConceptDwell = KindlessPayload('concept.dwell', z.object({
  conceptId: z.string(),
  msOnScreen: z.number().int().nonnegative(),
}));
const ConceptHintRequested = KindlessPayload('concept.hint', z.object({
  conceptId: z.string(),
  attemptNumber: z.number().int().positive(),
}));

/* ----- Hint / dialogue ----- */
const DialogueAdvance = KindlessPayload('dialogue.advance', z.object({
  simId: z.string(),
  step: z.number().int().nonnegative(),
  direction: z.enum(['next', 'back']),
}));

const EventSchema = z.discriminatedUnion('kind', [
  SessionStart, SessionEnd,
  RouteEnter,
  SimMount, SimReset,
  TxnDrafted, TxnPosted, TxnRejected,
  ConceptDwell, ConceptHintRequested,
  DialogueAdvance,
]);

export type AcesEvent = z.infer<typeof EventSchema>;
export type AcesEventInput = AcesEvent;

export const EnvelopeSchema = EventBaseSchema.and(EventSchema);
export type AcesEnvelope = z.infer<typeof EnvelopeSchema>;

export { EventSchema };
